import React, { useEffect, useRef, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import js_beautify from "js-beautify";
import { Box, ButtonGroup, TextField, Typography } from "@mui/material";
import {
  Block as BlockIcon, CloudUpload as CloudUploadIcon, DoneAll as DoneAllIcon,
  GetApp as GetAppIcon, Keyboard as KeyboardIcon, Link as LinkIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import RegularButton from "components/CustomButtons/Button";
import CardTable from "components/CardTable/CardTable";
import { ModalV2 } from "components/Modal/ModalV2";
import { HeadersTabs } from "./HeadersTabs";
import { InputColumns } from "./InputColumns";
import ListIcons from "./ListIcons";
import { createReactName, generateCardTemplate } from "./Template";
import { safeRemoveChild } from "helpers/safeRemoveChild";
import { useToggle } from "hooks/useToggle";
import { useForm } from "hooks/useForm";

const style = {
  width: "100%",
  height: "80vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
};

const iconStyle = {
  margin: 0,
};

export const ExcelTable = () => {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("No hay archivos seleccionados");
  const [headersAndAccessors, setHeadersAndAccessors] = useState([
    { Header: "Ejemplo", active: true },
  ]);
  const [excelData, setExcelData] = useState([]);
  const [fileColumnJs, setFileColumnJs] = useState("");
  const [open, toggleOpen] = useToggle();
  const [openIcons, toggleOpenIcons] = useToggle();
  const [openInputs, setOpenInputs] = useToggle();
  const { inputValues, handleInputOnChange } = useForm({
    fileInput: "columns",
    reactName: "Component",
    searchIcon: "",
  });
  const { fileInput, searchIcon, reactName } = inputValues;
  const [reactFormatName, setReactFormatName] = useState(reactName);
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = handleFileLoad;
      reader.readAsArrayBuffer(file);
    } else {
      resetState();
    }
  };

  const handleFileLoad = (e) => {
    const data = new Uint8Array(e.target.result);
    const workbook = XLSX.read(data, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const excelData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (excelData.length > 0) {
      const headers = excelData[0];
      const accessors = headers.map((header) => header);

      const newHeadersAndAccessors = headers.map((header, index) => ({
        Header: header,
        accessor: accessors[index],
        active: false,
      }));

      setHeadersAndAccessors([
        ...newHeadersAndAccessors,
        { Header: "Acciones", active: true },
      ]);

      const formattedExcelData = excelData.slice(1).map((row) => {
        const rowData = {};
        headers.forEach((header, index) => {
          rowData[accessors[index]] = row[index];
        });
        return rowData;
      });

      setExcelData(formattedExcelData);
    }
  };

  const resetState = () => {
    setFileName("No hay archivos seleccionados");
    setHeadersAndAccessors([]);
    setExcelData([]);
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleColumn = (column) => {
    setHeadersAndAccessors((prevHeaders) =>
      prevHeaders.map((head) =>
        head.Header === column ? { ...head, active: !head.active } : head
      )
    );
  };

  const handleRestore = () => {
    setHeadersAndAccessors((prevHeaders) =>
      prevHeaders.map((item) =>
        item.Header === "Acciones"
          ? { ...item, active: true }
          : { ...item, active: false }
      )
    );
  };

  const handleSelectAll = () => {
    setHeadersAndAccessors((prevHeaders) =>
      prevHeaders.map((item) => ({ ...item, active: true }))
    );
  };

  const handleFileColumnJs = () => {
    const data = headersAndAccessors
      .filter((item) => item.active)
      .map((item) => ({
        Header: item.Header,
        accessor: "",
        filterable: true,
        filterMethod: `(filter, row) => {
          return row.${item.accessor}.toLowerCase().includes(filter.value.toLowerCase());
        }`,
      }));

    const fileContent = JSON.stringify(data);
    setFileColumnJs(fileContent);
    toggleOpen();
  };

  const handleDownloadFile = () => {
    const fileColumnsJsFormatted = js_beautify(fileColumnJs);
    const jsFile = fileInput.split(" ").join("");
    const reactFile = generateCardTemplate(reactFormatName, fileInput);
    const addColVar = `export const ${jsFile} = ${fileColumnsJsFormatted}`;

    const filesContent = [
      { fileName: `${jsFile}.js`, content: addColVar },
      { fileName: `${reactFormatName}.jsx`, content: reactFile },
    ];

    const zip = new JSZip();

    filesContent.forEach((file) => {
      zip.file(file.fileName, file.content);
    });

    zip.generateAsync({ type: "blob" }).then((blob) => {
      const element = document.createElement("a");
      const url = URL.createObjectURL(blob);
      element.href = url;
      element.download = "bundleTable.zip";
      document.body.appendChild(element);
      element.click();
      safeRemoveChild(element);
      URL.revokeObjectURL(url);
    });
  };

  const handleColumnsInputs = (heads) => {
    const accessors = heads.map((header) => header);
    const newHeadersAndAccessors = heads.map((header, index) => ({
      Header: header,
      accessor: accessors[index],
      active: false,
    }));

    setHeadersAndAccessors([
      ...newHeadersAndAccessors,
      { Header: "Acciones", active: true },
    ]);

    const formattedExcelData = excelData.slice(1).map((row) => {
      const rowData = {};
      heads.forEach((header, index) => {
        rowData[accessors[index]] = row[index];
      });
      return rowData;
    });

    setExcelData(formattedExcelData);
    setOpenInputs();
  };

  const activeHeaders = useMemo(
    () => headersAndAccessors.filter((item) => item.active),
    [headersAndAccessors]
  );

  useEffect(() => {
    setReactFormatName(createReactName(reactName));
  }, [reactName]);

  return (
    <div style={style}>
      <CardTable
        columns={activeHeaders.length > 0 ? activeHeaders : headersAndAccessors}
        data={excelData}
        hasControl
        hasHeader
        icon={<TableChartIcon />}
        title={fileName}
        titleBlock
        tableContent={headersAndAccessors.map((column) => (
          <HeadersTabs
            key={column.Header}
            header={column.Header}
            isActive={column.active}
            action={handleColumn}
          />
        ))}
        blockBtn
        controls={
          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <ButtonGroup>
              <RegularButton
                color="rose"
                justIcon
                round
                tooltip="JSON Viewer"
                href="https://jsonformatter.curiousconcept.com/#"
                target="_blank"
              >
                <LinkIcon style={iconStyle} />
              </RegularButton>
              <RegularButton
                color="warning"
                justIcon
                round
                onClick={handleRestore}
                tooltip="Remover todos"
                disabled={headersAndAccessors.length < 2}
              >
                <BlockIcon style={iconStyle} />
              </RegularButton>
              <RegularButton
                color="success"
                justIcon
                round
                onClick={handleSelectAll}
                tooltip="Seleccionar todos"
                disabled={headersAndAccessors.length < 2}
              >
                <DoneAllIcon style={iconStyle} />
              </RegularButton>
              <RegularButton
                color="linkedin"
                justIcon
                round
                onClick={handleFileColumnJs}
                tooltip="Descargar bundle"
                disabled={headersAndAccessors.length < 2}
              >
                <GetAppIcon style={iconStyle} />
              </RegularButton>
              <RegularButton
                color="tumblr"
                justIcon
                round
                onClick={setOpenInputs}
                tooltip="Agregar Columnas"
              >
                <KeyboardIcon style={iconStyle} />
              </RegularButton>
              <RegularButton
                color="info"
                justIcon
                round
                onClick={handleButtonClick}
                tooltip="Cargar archivo"
              >
                <CloudUploadIcon style={iconStyle} />
              </RegularButton>
            </ButtonGroup>
          </>
        }
      />
      <ModalV2
        open={open}
        onClose={toggleOpen}
        title="Descargar tableBundle.zip"
        toRight
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 15,
          }}
        >
          <TextField
            label="Archivo JS"
            variant="outlined"
            name="fileInput"
            value={fileInput}
            onChange={handleInputOnChange}
            fullWidth
          />
          <TextField
            label="Componente React JS"
            variant="outlined"
            name="reactName"
            value={reactName}
            onChange={handleInputOnChange}
            fullWidth
          />

          <Typography variant="body1" color="primary" component="p">
            Lista de Archivos.
          </Typography>
          <Typography variant="body2" component="p">
            {fileInput}.js
          </Typography>
          <Typography variant="body2" component="p">
            {reactFormatName}.jsx
          </Typography>
          <RegularButton
            fullWidth
            onClick={handleDownloadFile}
            color="facebook"
            tooltip="Descargar"
          >
            Descargar archivo
          </RegularButton>
        </Box>
      </ModalV2>
      <ModalV2
        open={openIcons}
        onClose={toggleOpenIcons}
        title="Iconos"
        toRight
      >
        <TextField
          variant="outlined"
          name="searchIcon"
          value={searchIcon}
          onChange={handleInputOnChange}
          fullWidth
        />
        <ListIcons />
      </ModalV2>
      <ModalV2
        open={openInputs}
        onClose={setOpenInputs}
        title="Agregar Columnas"
        toRight
      >
        <InputColumns onSubmit={handleColumnsInputs} />
      </ModalV2>
    </div>
  );
};
